import type { Point2 } from '../geometry/point';

/**
 * What a snap target is.
 *
 * Ranked, not just labelled: when several targets sit within the pick radius the
 * user almost always means the endpoint, so kind beats distance. Snapping to a
 * midpoint two pixels nearer than the corner you were aiming at is the single most
 * irritating thing a CAD snap can do.
 */
export type SnapKind = 'endpoint' | 'midpoint' | 'center' | 'edge';

/**
 * Lower wins. `edge` — the nearest point anywhere along a line — is last on
 * purpose: it is available almost everywhere near geometry, so if it competed on
 * distance it would shadow every corner and centre the user was actually aiming
 * for. It is a fallback for "somewhere along this line", not a rival.
 */
const RANK: Readonly<Record<SnapKind, number>> = {
  endpoint: 0,
  center: 1,
  midpoint: 2,
  edge: 3,
};

export type SnapCandidate = {
  readonly point: Point2;
  readonly kind: SnapKind;
  /** Layer the target belongs to, so hidden geometry cannot be snapped to. */
  readonly layer: number;
  /** The caller's stable address for the entity the target came from, if any. */
  readonly entityId?: string;
};

/** `T` lets a caller attach its own data to a candidate and get it back. */
export type SnapIndex<T extends SnapCandidate = SnapCandidate> = {
  readonly size: number;
  /**
   * Best target within `tolerance` world units of `point`, or `null`.
   * Higher-ranked kinds win ties; distance decides within a kind.
   */
  nearest(
    point: Point2,
    tolerance: number,
    hiddenLayers?: ReadonlySet<number>,
  ): T | null;
};

const EMPTY_INDEX: SnapIndex<never> = { size: 0, nearest: () => null };

/**
 * Hash-grid index over snap targets.
 *
 * Cell size is set at build time from the drawing's extent rather than from the
 * query tolerance, because the tolerance changes with zoom on every query and
 * rebuilding the grid per pointer move would cost far more than the widened scan.
 */
export const buildSnapIndex = <T extends SnapCandidate>(
  candidates: readonly T[],
  cellSize: number,
): SnapIndex<T> => {
  if (candidates.length === 0 || !(cellSize > 0)) {
    return EMPTY_INDEX;
  }

  const buckets = new Map<string, number[]>();
  const keyOf = (cx: number, cy: number) => `${cx},${cy}`;

  candidates.forEach((candidate, index) => {
    const key = keyOf(
      Math.floor(candidate.point[0] / cellSize),
      Math.floor(candidate.point[1] / cellSize),
    );
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(index);
    } else {
      buckets.set(key, [index]);
    }
  });

  return {
    size: candidates.length,

    nearest(point, tolerance, hiddenLayers) {
      const radius = Math.ceil(tolerance / cellSize);
      const centerX = Math.floor(point[0] / cellSize);
      const centerY = Math.floor(point[1] / cellSize);
      const toleranceSq = tolerance * tolerance;

      let best: T | null = null;
      let bestRank = Infinity;
      let bestDistanceSq = Infinity;

      for (let dx = -radius; dx <= radius; dx += 1) {
        for (let dy = -radius; dy <= radius; dy += 1) {
          const bucket = buckets.get(keyOf(centerX + dx, centerY + dy));
          if (!bucket) {
            continue;
          }
          for (let index = 0; index < bucket.length; index += 1) {
            const candidate = candidates[bucket[index]];
            if (hiddenLayers?.has(candidate.layer)) {
              continue;
            }
            const distanceSq =
              (candidate.point[0] - point[0]) ** 2 +
              (candidate.point[1] - point[1]) ** 2;
            if (distanceSq > toleranceSq) {
              continue;
            }
            const rank = RANK[candidate.kind];
            if (
              rank < bestRank ||
              (rank === bestRank && distanceSq < bestDistanceSq)
            ) {
              best = candidate;
              bestRank = rank;
              bestDistanceSq = distanceSq;
            }
          }
        }
      }

      return best;
    },
  };
};
